import {
  CATCH_BOARD_BUYER_EMAIL_MAX_LENGTH,
  CATCH_BOARD_BUYER_NAME_MAX_LENGTH,
  CATCH_BOARD_BUYER_PHONE_MAX_LENGTH,
  CATCH_BOARD_BUYER_ROLE_MAX_LENGTH,
  CATCH_BOARD_MAX_BUYERS_PER_ROLE,
} from 'src/screens/catch-board/catchBoardConstants'

/**
 * @param {object} contact
 * @returns {{ accountId: string|null, name: string, email: string, role: string, phone: string }}
 */
export function normalizeCatchBoardBuyer(contact = {}) {
  const accountId = contact.accountId ? String(contact.accountId) : null

  return {
    accountId,
    name: String(contact.name || '')
      .trim()
      .slice(0, CATCH_BOARD_BUYER_NAME_MAX_LENGTH),
    email: String(contact.email || '')
      .trim()
      .slice(0, CATCH_BOARD_BUYER_EMAIL_MAX_LENGTH),
    role: String(contact.role || '')
      .trim()
      .slice(0, CATCH_BOARD_BUYER_ROLE_MAX_LENGTH),
    phone: String(contact.phone || '')
      .trim()
      .slice(0, CATCH_BOARD_BUYER_PHONE_MAX_LENGTH),
  }
}

/**
 * Stable key for dedupe / React lists.
 * @param {object} buyer
 */
export function getCatchBoardBuyerKey(buyer = {}) {
  const normalized = normalizeCatchBoardBuyer(buyer)
  if (normalized.accountId) {
    return `account:${normalized.accountId}`
  }
  const email = normalized.email.toLowerCase()
  const name = normalized.name.toLowerCase()
  if (email || name) {
    return `external:${email}|${name}`
  }
  return `empty:${normalized.role}|${normalized.phone}`
}

/**
 * @param {object[]} buyers
 * @returns {object[]}
 */
export function normalizeCatchBoardBuyers(buyers = []) {
  const seen = new Set()
  const result = []

  ;(Array.isArray(buyers) ? buyers : []).forEach(entry => {
    const buyer = normalizeCatchBoardBuyer(entry)
    if (!buyer.name && !buyer.email) {
      return
    }
    const key = getCatchBoardBuyerKey(buyer)
    if (seen.has(key)) {
      return
    }
    seen.add(key)
    result.push(buyer)
  })

  return result
}

/**
 * Map a Spartacus venue user into a Catch Board contact snapshot.
 * @param {object} user
 */
export function spartacusUserToCatchBoardBuyer(user = {}) {
  return normalizeCatchBoardBuyer({
    accountId: user.accountId || null,
    name: user.name || '',
    email: user.email || '',
    role: user.role || '',
    phone: user.phone || '',
  })
}

/**
 * @param {object} buyer
 */
export function formatCatchBoardBuyerLabel(buyer = {}) {
  const normalized = normalizeCatchBoardBuyer(buyer)
  const parts = []
  if (normalized.name) {
    parts.push(normalized.name)
  }
  if (normalized.email) {
    parts.push(normalized.email)
  }
  if (normalized.role) {
    parts.push(normalized.role)
  }
  return parts.join(' · ') || 'Buyer'
}

/**
 * @param {object} buyer
 * @returns {string|null}
 */
export function validateCatchBoardBuyer(buyer = {}) {
  const normalized = normalizeCatchBoardBuyer(buyer)
  if (!normalized.name && !normalized.email) {
    return 'Buyer needs a name or email'
  }
  return null
}

/**
 * @param {object[]} buyers
 * @param {object} buyer
 * @param {{ max?: number }} [options]
 * @returns {{ buyers: object[], error?: string }}
 */
export function addCatchBoardBuyer(
  buyers = [],
  buyer,
  { max = CATCH_BOARD_MAX_BUYERS_PER_ROLE } = {},
) {
  const validationError = validateCatchBoardBuyer(buyer)
  if (validationError) {
    return { buyers: normalizeCatchBoardBuyers(buyers), error: validationError }
  }

  const existing = normalizeCatchBoardBuyers(buyers)
  const nextBuyer = normalizeCatchBoardBuyer(buyer)
  const key = getCatchBoardBuyerKey(nextBuyer)

  if (existing.some(row => getCatchBoardBuyerKey(row) === key)) {
    return { buyers: existing, error: 'Buyer already added' }
  }

  if (existing.length >= max) {
    return {
      buyers: existing,
      error: `You can add at most ${max} buyers in this list`,
    }
  }

  return { buyers: [...existing, nextBuyer] }
}

/**
 * @param {object[]} buyers
 * @param {string} buyerKey
 */
export function removeCatchBoardBuyer(buyers = [], buyerKey) {
  const target = String(buyerKey || '')
  return normalizeCatchBoardBuyers(buyers).filter(
    row => getCatchBoardBuyerKey(row) !== target,
  )
}

/**
 * API-ready buyer arrays (drop blanks).
 * @param {object[]} buyers
 */
export function serializeCatchBoardBuyers(buyers = []) {
  return normalizeCatchBoardBuyers(buyers).map(buyer => ({
    accountId: buyer.accountId || null,
    name: buyer.name,
    email: buyer.email,
    role: buyer.role,
    phone: buyer.phone,
  }))
}

export function emptyCatchBoardExternalBuyerForm() {
  return {
    name: '',
    email: '',
    role: '',
    phone: '',
  }
}
